import { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { TimesheetExcludedUsersApi } from 'api/timesheets/excluded-users/api';
import { toastService } from 'components/molecules/toast';
import { useTranslate } from 'hooks/use-translation';
import { add, added, empty, unadded } from './core';
import { TimesheetExcludedUsers, UserId } from './types';
import { getTimesheetExcludedUsersKey as queryKey } from './use-excluded-users';

export const useAddExcludedUsers = (): ((userIds: UserId[]) => void) => {
  const queryClient = useQueryClient();
  const updateWithAddingState = useCallback(
    (userIds: UserId[]) =>
      queryClient.setQueryData<TimesheetExcludedUsers>(queryKey(), (state) =>
        add(userIds)(state ?? empty()),
      ),
    [queryClient],
  );
  const updateWithAddedState = useCallback(
    (userIds: UserId[]) =>
      queryClient.setQueryData<TimesheetExcludedUsers>(queryKey(), (state) =>
        added(userIds)(state ?? empty()),
      ),
    [queryClient],
  );
  const updateWitUnaddedState = useCallback(
    (userIds: UserId[]) =>
      queryClient.setQueryData<TimesheetExcludedUsers>(queryKey(), (state) =>
        unadded(userIds)(state ?? empty()),
      ),
    [queryClient],
  );
  const translate = useTranslate();
  const showSuccessMessage = useCallback(
    (count: number) => {
      toastService({
        toastContainer: {},
        title: translate(
          'settings.timesheets.controls.exclusion.add.success.title',
        ),
        message: translate(
          'settings.timesheets.controls.exclusion.add.success.description',
          { count },
        ),
      }).success();
    },
    [translate],
  );
  const showFailureMessage = useCallback(
    (count: number) => {
      toastService({
        toastContainer: {},
        title: translate(
          'settings.timesheets.controls.exclusion.add.failure.title',
        ),
        message: translate(
          'settings.timesheets.controls.exclusion.add.failure.description',
          { count },
        ),
      }).error();
    },
    [translate],
  );
  const addExcludedUsers = useCallback(
    (userIds: UserId[]) => {
      updateWithAddingState(userIds);
      TimesheetExcludedUsersApi.add(userIds)
        .then(() => {
          updateWithAddedState(userIds);
          showSuccessMessage(userIds.length);
        })
        .catch(() => {
          updateWitUnaddedState(userIds);
          showFailureMessage(userIds.length);
        });
    },
    [
      showFailureMessage,
      showSuccessMessage,
      updateWitUnaddedState,
      updateWithAddedState,
      updateWithAddingState,
    ],
  );

  return addExcludedUsers;
};
